import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { Sparkline } from '@netbasequid/chart'
import { getPercentOfTotal } from 'components/insightContent/utils/utils'
import SentenceSoundbite from 'components/insightContent/Soundbite/SentenceSoundbite'
import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { Theme } from 'features/ui/types'
import { measureToSparklineColorMap } from 'components/insightContent/visualization/Overview/SparklineOverview'
import { MeasureType } from 'enums'
import SpinningQ from 'components/share/SpinningQ'
import LinkButton from 'components/share/LinkButton'
import openHelpPage from 'utils/helpLinkUtil'
import { simpleNumberFormatter } from 'utils/numberFormatterUtil'
import { setAutoId, AutomationId } from 'utils/automationUtil'
import DeletedPost from './DeletedPost'
import useTermsAndSoundBiteData from './hooks/useTermsAndSoundBiteData'

const ItemStyle = css<{ $theme: SystemTheme }>`
  display: flex;
  gap: 36px;
  border-bottom: 1px solid ${({ $theme }) => $theme.common.divider[100]};
`

const Container = styled.div.attrs(() => setAutoId(AutomationId.fluctuationSummary.popularTerms))`
  flex: 1;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
`

const Item = styled.div.attrs(() => setAutoId(AutomationId.fluctuationSummary.popularTermsItem))<{
  $theme: SystemTheme
}>`
  ${ItemStyle}
  padding: 8px;
`

const Term = styled.div<{ $theme: SystemTheme }>`
  flex: 1;
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  word-break: break-all;
`

const Trend = styled.div`
  flex: 1;
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Count = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
`

const Percent = styled.div<{ $theme: SystemTheme }>`
  color: ${({ $theme }) => $theme.text.placeholder};
`

const Chart = styled.div`
  height: 32px;
`

const SoundBite = styled.div`
  flex: 5;

  & > div {
    margin: 0px;
    & > div {
      border-top: none;
      padding: 0px;
    }
  }
`

const HeaderTextStyle = css<{ $theme: SystemTheme }>`
  color: ${({ $theme }) => $theme.text.placeholder};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
`

const Header = styled.div.attrs(() =>
  setAutoId(AutomationId.fluctuationSummary.popularTermsHeader)
)<{
  $theme: SystemTheme
}>`
  ${ItemStyle}
  padding: 0px 8px 4px 8px;
  ${HeaderTextStyle}
  ${Term} {
    ${HeaderTextStyle}
  }
`

const StyledSpinningQ = styled(SpinningQ)`
  flex: 1;
`

const EmptyResultMessage = styled.div<{ $theme: SystemTheme }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ $theme }) => $theme.text.primary};
  gap: 4px;
`

const TermsAndSoundBite = () => {
  const { t } = useTranslation()
  const mode = useDisplayTheme() as Theme
  const theme = useSysTheme()
  const {
    insightSets,
    totalMetricValue,
    sampleSoundBiteData,
    metricValueData,
    isLoading,
    isEmptyResult,
    isSucceeded
  } = useTermsAndSoundBiteData()

  return (
    <Container>
      <Header $theme={theme}>
        <Term $theme={theme}>{t('popularTerms')}</Term>
        <Trend>{t('post_other')}</Trend>
        <SoundBite>{t('sampleSoundBite')}</SoundBite>
      </Header>
      {isLoading && <StyledSpinningQ size={32} color={theme.text.primary} />}
      {isEmptyResult && (
        <EmptyResultMessage $theme={theme}>
          <img
            width="44px"
            height="48px"
            src={`${process.env.PUBLIC_URL}/assets/no-data-found-${mode}.svg`}
            alt="no data found"
          />
          <span>
            {t('noDataFoundInWidget')}&nbsp;
            <LinkButton onClick={() => openHelpPage(8187320)}>{t('moreDetails')}</LinkButton>
          </span>
        </EmptyResultMessage>
      )}
      {isSucceeded &&
        insightSets?.map((insight, index) => (
          <Item key={insight?.name} $theme={theme}>
            <Term $theme={theme}>{insight?.name}</Term>
            <Trend>
              <Count>
                {insight?.value && simpleNumberFormatter(insight.value)}
                &nbsp;
                <Percent $theme={theme}>
                  {t('percentOfTotal', {
                    percent:
                      totalMetricValue !== undefined
                        ? getPercentOfTotal(insight.value!, totalMetricValue)
                        : undefined,
                    interpolation: { escapeValue: false }
                  })}
                </Percent>
              </Count>
              <Chart>
                <Sparkline
                  mode={mode}
                  data={
                    (metricValueData?.[index]?.results?.[0]?.data?.metrics?.[0]?.dataset?.[0].set ||
                      []) as number[]
                  }
                  colorSettings={measureToSparklineColorMap[MeasureType.POSTS]}
                />
              </Chart>
            </Trend>
            <SoundBite>
              {sampleSoundBiteData[index] ? (
                <SentenceSoundbite
                  data={sampleSoundBiteData[index]!}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  serialNumber={index}
                />
              ) : (
                <DeletedPost />
              )}
            </SoundBite>
          </Item>
        ))}
    </Container>
  )
}

export default TermsAndSoundBite
